import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import BsDropdown from "@stadium/ui/src/components/dropdown/bs-dropdown";

import { useProductDetailContext } from "@contexts/product-detail-context";
import { getIsPricelessShop } from "@selectors/store";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { ANALYTICS_EVENT_ACTION_SELECTED } from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_ATTRIBUTE, ANALYTICS_ATTRIBUTE_TYPE } from "@constants/analytics/shop";
import { getProductType, getProductCategory } from "@helpers/products/types";

import styles from "./main.module.scss";

const MedleySizeOptions = ({
  title,
  options,
  handleChange,
  selectedData,
  productId,
  product,
  disabled = false,
}) => {
  const {
    isOptionSelected,
    optionSelectionError: showErrorMessage,
    setIsOptionSelected,
  } = useProductDetailContext();
  const isPricelessShop = useSelector(getIsPricelessShop);

  const withAnalytics = useWithAnalytics();

  const [selectedOption, setSelectedOption] = useState(null);
  const hasAutoSelectedRef = useRef(false);

  const optionsKey = options.map(({ id }) => id).join(",");

  useEffect(() => {
    hasAutoSelectedRef.current = false;
  }, [optionsKey]);

  useEffect(() => {
    setSelectedOption(selectedData);
    if (!isEmpty(selectedData)) {
      setIsOptionSelected(true);
    }
  }, [selectedData, setIsOptionSelected]);

  const renderLabelContent = (data) => {
    const isActive = data.id === selectedOption?.id;
    let className = styles["labelContentWrap"];
    className += isActive ? " activeBg" : " ";
    return (
      <div className={className}>
        <span className={styles.dataAmount}>{data.displayName}</span>
        <span className={styles.amountIn}>{data.amountIn}</span>
      </div>
    );
  };

  const dropdownOptions = useMemo(() => {
    return options.map((data) => ({
      key: `${data.name}-${data.id}-${data.variantId}`,
      label: renderLabelContent(data),
      data: data,
    }));
  }, [options, selectedOption]);

  const dropDownOptionClickHandler = (selectedVal, track = true) => {
    setIsOptionSelected(true);
    setSelectedOption(selectedVal.data);

    if (track) {
      withAnalytics(undefined, {
        target: ANALYTICS_TARGET_ATTRIBUTE,
        action: ANALYTICS_EVENT_ACTION_SELECTED,
        track: true,
        eventProperties: {
          product_id: productId,
          attribute_type: ANALYTICS_ATTRIBUTE_TYPE.BOX_SIZE,
          attribute_value: selectedVal.data.displayName,
          product_type: getProductType(product),
          category: getProductCategory(product),
        },
      })();
    }

    return handleChange(selectedVal.data);
  };

  useEffect(() => {
    if (hasAutoSelectedRef.current || !isPricelessShop) return;

    const selectedVal = dropdownOptions?.[dropdownOptions?.length - 1];
    if (selectedVal?.data) {
      hasAutoSelectedRef.current = true;
      dropDownOptionClickHandler(selectedVal, false);
    }
  }, [isPricelessShop, dropdownOptions]);

  const dropDownInitialValue = useMemo(() => {
    if (dropdownOptions.length === 1) {
      return 0;
    }
    if (!isEmpty(selectedData) && selectedData?.id !== selectedOption?.id) {
      return options.findIndex((opt) => opt.id === selectedData?.id);
    }

    const currentIndex = options.findIndex((opt) => opt.id === selectedOption?.id);

    return currentIndex === -1 ? null : currentIndex;
  }, [selectedData, selectedOption, dropdownOptions, options]);

  useEffect(() => {
    if (dropdownOptions.length === 1) {
      setIsOptionSelected(true);
    }
  }, [dropdownOptions.length, setIsOptionSelected]);

  if (options.length === 0 || isPricelessShop) return null;

  return (
    <>
      <div className={styles.boxLabel}>{title}</div>
      <BsDropdown
        id="dropdown-basic"
        initValue={dropDownInitialValue}
        placeHolder="Select"
        options={dropdownOptions}
        onChange={dropDownOptionClickHandler}
        customClass={styles.bsDropdown}
        toggleBtnClass={`${styles.rdDropdownToggle} ${
          showErrorMessage && !isOptionSelected ? styles["btnError"] : ""
        } `}
        dropdownClass={styles.sizeDropdownMenu}
        toggleArrowClass={styles.iconChevronDown}
        disabled={disabled}
      />
      {showErrorMessage && !isOptionSelected && (
        <span className={styles.addToBagBtnError}>Please select a value.</span>
      )}
    </>
  );
};

export default MedleySizeOptions;
